import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type { AuthClient } from './types';

/**
 * Merchant auth-client RTK Query slice. Lives separately from merchantApi
 * because it talks to the user-service (`/v1/clients`), not the merchant
 * service, and because the plaintext-secret lifecycle is delicate enough
 * to be worth giving its own surface.
 */
export const merchantAuthClientApi = createApi({
  reducerPath: 'merchantAuthClientApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['AuthClient'],
  endpoints: (builder) => ({
    /**
     * GET /user/v1/clients/find/{role}/{id} — fetch the auth client owned by
     * a given entity (merchant/vendor). Returns `{}` when no client exists yet.
     */
    getAuthClient: builder.query<AuthClient, { entityType: string; entityId: string }>({
      query: ({ entityType, entityId }) => ({
        url: `${services.user}/v1/clients/find/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { entityType, entityId }) => [
        { type: 'AuthClient', id: `${entityType}:${entityId}` },
      ],
    }),
    /** POST /user/v1/clients — create a new auth client for an entity. */
    createAuthClient: builder.mutation<AuthClient, { role: string; id: string }>({
      query: ({ role, id }) => ({
        url: `${services.user}/v1/clients`,
        method: 'POST',
        body: { role, id },
      }),
      invalidatesTags: (_r, _e, { role, id }) => [
        { type: 'AuthClient', id: `${role}:${id}` },
      ],
    }),
    /**
     * PUT /user/v1/clients/{clientId}/secret — regenerate the secret.
     * Returns the new secret in plaintext (single-show).
     */
    regenerateAuthClientSecret: builder.mutation<
      AuthClient,
      { clientId: string; entityType: string; entityId: string }
    >({
      query: ({ clientId }) => ({
        url: `${services.user}/v1/clients/${encodeURIComponent(clientId)}/secret`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (_r, _e, { entityType, entityId }) => [
        { type: 'AuthClient', id: `${entityType}:${entityId}` },
      ],
    }),
    /** DELETE /user/v1/clients/{clientId} */
    deleteAuthClient: builder.mutation<
      void,
      { clientId: string; entityType: string; entityId: string }
    >({
      query: ({ clientId }) => ({
        url: `${services.user}/v1/clients/${encodeURIComponent(clientId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { entityType, entityId }) => [
        { type: 'AuthClient', id: `${entityType}:${entityId}` },
      ],
    }),
  }),
});

export const {
  useGetAuthClientQuery,
  useCreateAuthClientMutation,
  useRegenerateAuthClientSecretMutation,
  useDeleteAuthClientMutation,
} = merchantAuthClientApi;
